import { Controller } from '@hotwired/stimulus'
import { createCalendar, createViewMonthGrid } from '@schedule-x/calendar'
import { createEventModalPlugin } from '@schedule-x/event-modal'
import 'temporal-polyfill/global'

interface CalendarEventPayload {
  id: number
  title: string
  start: string
  end: string
  people: string[]
  calendarId: string
  projectsUrl: string
}

const COLLAPSED_EVENTS_PER_DAY = 3
const EXPANDED_EVENTS_PER_DAY = 100

export default class extends Controller {
  static values = {
    events: Array,
    calendars: Object,
    showLess: { type: String, default: 'Show less' }
  }

  declare eventsValue: CalendarEventPayload[]
  declare calendarsValue: Record<string, unknown>
  declare showLessValue: string

  calendar: ReturnType<typeof createCalendar> | null = null
  resizeObserver: ResizeObserver | null = null
  expanded = false
  showLessButton: HTMLButtonElement | null = null

  connect (): void {
    const monthView = createViewMonthGrid()
    const events = this.eventsValue.map((event) => ({
      id: event.id,
      title: event.title,
      start: Temporal.PlainDate.from(event.start),
      end: Temporal.PlainDate.from(event.end),
      people: event.people,
      calendarId: event.calendarId,
      _projectsUrl: event.projectsUrl
    }))

    this.calendar = createCalendar({
      views: [monthView],
      defaultView: monthView.name,
      events,
      calendars: this.calendarsValue as any,
      isDark: document.documentElement.classList.contains('dark'),
      isResponsive: false,
      monthGridOptions: {
        nEventsPerDay: COLLAPSED_EVENTS_PER_DAY
      },
      datePicker: {
        teleportTo: document.body
      },
      plugins: [createEventModalPlugin()],
      callbacks: {
        onEventClick: (calendarEvent) => {
          const projectsUrl = (calendarEvent as { _projectsUrl?: string })._projectsUrl
          this.element.setAttribute('data-active-projects-url', projectsUrl ?? '')
        },
        onClickPlusEvents: () => {
          this.expandEvents()
        },
        onRangeUpdate: () => {
          if (this.expanded) this.collapseEvents()
        }
      }
    })

    this.calendar.render(this.element as HTMLElement)

    this.resizeObserver = new ResizeObserver(() => {
      window.dispatchEvent(new Event('resize'))
    })
    this.resizeObserver.observe(this.element)
  }

  expandEvents (): void {
    if ((this.calendar == null) || this.expanded) return

    this.expanded = true
    this.setEventsPerDay(EXPANDED_EVENTS_PER_DAY)
    this.insertShowLessButton()
  }

  collapseEvents (): void {
    if ((this.calendar == null) || !this.expanded) return

    this.expanded = false
    this.setEventsPerDay(COLLAPSED_EVENTS_PER_DAY)
    this.showLessButton?.remove()
    this.showLessButton = null
  }

  setEventsPerDay (nEventsPerDay: number): void {
    ;(this.calendar as unknown as {
      $app: { config: { monthGridOptions: { value: { nEventsPerDay: number } } } }
    }).$app.config.monthGridOptions.value = { nEventsPerDay }
  }

  insertShowLessButton (): void {
    if (this.showLessButton != null) return

    const headers = this.element.querySelectorAll('.sx__calendar-header-content')
    const header = headers.length > 1 ? headers.item(1) : headers.item(0)
    if (header == null) return

    this.showLessButton = document.createElement('button')
    this.showLessButton.type = 'button'
    this.showLessButton.className = 'sx__button sx__ripple widget-calendar__show-less'
    this.showLessButton.textContent = this.showLessValue
    this.showLessButton.addEventListener('click', () => this.collapseEvents())
    header.prepend(this.showLessButton)
  }

  projectsClick (event: MouseEvent): void {
    const target = event.target as HTMLElement
    if (target.closest('.sx__event-modal__people') == null) return

    const url = this.element.getAttribute('data-active-projects-url')
    if (url != null && url !== '') window.location.href = url
  }

  disconnect (): void {
    this.showLessButton?.remove()
    this.showLessButton = null
    this.resizeObserver?.disconnect()
    this.calendar?.destroy()
    this.calendar = null
  }
}
